/**
 * Legacy Documents Page — 3-column layout (route /legacy-documents).
 * Document List | PDF Viewer | Details Panel. Uses the backend API.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parseDate } from "@internationalized/date";
import { AlertCircle, AlertTriangle, File06, FileCheck02, FilterLines, LayoutLeft, SearchLg, Trash01, XClose } from "@untitledui/icons";
import { PDFDocument } from "pdf-lib";
import type { DateValue } from "react-aria-components";
import { useSearchParams } from "react-router-dom";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Tab, TabList, TabPanel, Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import { useDocumentsApi } from "@/services/api/documents-api";
import { useFilesApi } from "@/services/api/files-api";
import { usePlanEntitlements } from "@/services/api/plans-api";
import { useRoutingCodesApi } from "@/services/api/routing-codes-api";
import {
    type CustomFieldDefinitionDto,
    type CustomFieldValueDto,
    type DocumentDetailDto,
    type DocumentListItemDto,
    DocumentStatusEnum,
    FieldTypeEnum,
    type UpdateCustomFieldValueDto,
    type UpdateCustomFieldsRequestDto,
    getCustomFieldDtoDisplayValue,
} from "@/types/api/documents";
import type { RoutingCodeDto } from "@/types/api/routing-codes";
import { exportDocumentsByType, exportDocumentsToCsv } from "@/utils/csv-export";
import { cx } from "@/utils/cx";

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_FILTERS = [
    { id: "all", label: "All" },
    { id: DocumentStatusEnum.Pending, label: "Pending" },
    { id: DocumentStatusEnum.Parsed, label: "Parsed" },
    { id: DocumentStatusEnum.Queued, label: "Queued" },
    { id: DocumentStatusEnum.Exported, label: "Exported" },
    { id: DocumentStatusEnum.Error, label: "Error" },
    { id: DocumentStatusEnum.Rejected, label: "Rejected" },
] as const;

// =============================================================================
// HELPERS
// =============================================================================

type StatusFilter = "all" | DocumentStatusEnum;

/**
 * Get badge color for document status
 */
function getStatusColor(status: DocumentStatusEnum, errorCode?: string, hasRetryWarning?: boolean): "success" | "brand" | "gray-blue" | "error" | "warning" {
    if (hasRetryWarning) return "warning";
    if (errorCode) return "error";
    switch (status) {
        case DocumentStatusEnum.Parsed:
            return "success";
        case DocumentStatusEnum.Queued:
            return "brand";
        case DocumentStatusEnum.Exported:
            return "success";
        case DocumentStatusEnum.Pending:
            return "gray-blue";
        case DocumentStatusEnum.Error:
        case DocumentStatusEnum.Rejected:
            return "error";
        default:
            return "gray-blue";
    }
}

/**
 * Get display label for document status
 */
function getStatusLabel(status: DocumentStatusEnum, errorCode?: string, hasRetryWarning?: boolean): string {
    if (hasRetryWarning) return "Retrying";
    if (errorCode) return "Error";
    return status;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

/**
 * Format date with time for details view
 */
function formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

/**
 * Format value for display based on field type
 */
function formatFieldValue(value: string | number | boolean | null, fieldType?: string | FieldTypeEnum): string {
    if (value === null || value === undefined) return "-";

    switch (fieldType) {
        case FieldTypeEnum.Decimal:
        case "Decimal":
            return typeof value === "number"
                ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                  }).format(value)
                : String(value);
        case FieldTypeEnum.Integer:
        case "Integer":
            return typeof value === "number" ? new Intl.NumberFormat("en-US").format(value) : String(value);
        case FieldTypeEnum.Date:
        case "Date":
            return typeof value === "string" ? formatDate(value) : String(value);
        case FieldTypeEnum.Boolean:
        case "Boolean":
            return value ? "Yes" : "No";
        default:
            return String(value);
    }
}

// =============================================================================
// DOCUMENT LIST PANEL
// =============================================================================

interface DocumentListPanelProps {
    documents: DocumentListItemDto[];
    filteredDocuments: DocumentListItemDto[];
    selectedDocumentId: number | null;
    onDocumentClick: (documentId: number, event: React.MouseEvent) => void;
    filter: StatusFilter;
    onFilterChange: (filter: StatusFilter) => void;
    selectedDocumentIds: Set<number>;
    onDocumentCheckboxChange: (documentId: number, checked: boolean) => void;
    onSelectAll: (checked: boolean) => void;
    onMultiExport: () => void;
    isMultiExporting?: boolean;
    hasExternalExportEntitlement?: boolean;
    onMultiExternalExport?: () => void;
    isMultiExternalExporting?: boolean;
    onCollapse: () => void;
    multiExportError?: string | null;
}

function DocumentListPanel({
    documents,
    filteredDocuments,
    selectedDocumentId,
    onDocumentClick,
    filter,
    onFilterChange,
    selectedDocumentIds,
    onDocumentCheckboxChange,
    onSelectAll,
    onMultiExport,
    isMultiExporting = false,
    hasExternalExportEntitlement = false,
    onMultiExternalExport,
    isMultiExternalExporting = false,
    onCollapse,
    multiExportError,
}: DocumentListPanelProps) {
    // Build filter options with counts
    const filterOptions = useMemo(() => {
        return STATUS_FILTERS.map((f) => ({
            ...f,
            count:
                f.id === "all"
                    ? documents.length
                    : f.id === DocumentStatusEnum.Error
                      ? documents.filter((d) => d.status === DocumentStatusEnum.Error || !!d.errorCode).length
                      : documents.filter((d) => d.status === f.id && !d.errorCode).length,
        }));
    }, [documents]);

    // Calculate select all state (only consider selectable, non-error documents)
    const selectableDocuments = filteredDocuments.filter(
        (doc) => doc.status !== DocumentStatusEnum.Error && doc.status !== DocumentStatusEnum.Rejected && !doc.errorCode && doc.isProcessingEndStateReached,
    );
    const allSelected = selectableDocuments.length > 0 && selectableDocuments.every((doc) => selectedDocumentIds.has(doc.id));
    const someSelected = selectableDocuments.some((doc) => selectedDocumentIds.has(doc.id));
    const isIndeterminate = someSelected && !allSelected;

    return (
        <div className="flex h-full w-96 max-w-sm flex-shrink-0 flex-col border-r border-secondary bg-primary">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-secondary px-4 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Checkbox size="sm" isSelected={allSelected} isIndeterminate={isIndeterminate} onChange={(checked) => onSelectAll(checked)} />
                        <h2 className="text-lg font-semibold text-primary">Documents</h2>
                        <Badge size="sm" color="gray" type="color">
                            {filteredDocuments.length}
                        </Badge>
                    </div>

                    <div className="flex items-center gap-1">
                        {/* Filter Dropdown */}
                        <Dropdown.Root>
                            <ButtonUtility icon={FilterLines} size="sm" color="secondary" tooltip="Filter documents" />
                            <Dropdown.Popover>
                                <Dropdown.Menu
                                    selectedKeys={[filter]}
                                    onSelectionChange={(keys) => {
                                        const selectedKey = Array.from(keys).at(0) as StatusFilter;
                                        onFilterChange(selectedKey);
                                    }}
                                >
                                    {filterOptions.map((option) => (
                                        <Dropdown.Item
                                            key={option.id}
                                            id={option.id}
                                            label={option.label}
                                            addon={option.count > 0 ? option.count.toString() : undefined}
                                        />
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown.Root>

                        {/* Collapse Button */}
                        <ButtonUtility icon={LayoutLeft} size="sm" color="secondary" tooltip="Collapse panel" onClick={onCollapse} />
                    </div>
                </div>

                {/* Multi Export Button Row */}
                {selectedDocumentIds.size > 0 && (
                    <div className="mt-3 flex flex-col gap-2">
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                color="primary"
                                iconLeading={FileCheck02}
                                onClick={onMultiExport}
                                isDisabled={isMultiExporting}
                                isLoading={isMultiExporting}
                                className="flex-1"
                            >
                                CSV Export ({selectedDocumentIds.size})
                            </Button>
                            {hasExternalExportEntitlement && (
                                <Button
                                    size="sm"
                                    color="primary"
                                    iconLeading={FileCheck02}
                                    onClick={onMultiExternalExport}
                                    isDisabled={isMultiExternalExporting}
                                    isLoading={isMultiExternalExporting}
                                    className="flex-1"
                                >
                                    External Export ({selectedDocumentIds.size})
                                </Button>
                            )}
                        </div>
                        {multiExportError && <p className="mt-1 text-xs text-error-primary">{multiExportError}</p>}
                    </div>
                )}

                {/* Current Filter Display */}
                {filter !== "all" && (
                    <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs font-medium text-tertiary">{filterOptions.find((f) => f.id === filter)?.label}</span>
                    </div>
                )}
            </div>

            {/* Document List */}
            <div className="flex-1 overflow-y-auto">
                {filteredDocuments.map((item) => {
                    // Documents in error/rejected state are not checkable
                    const isErrorDocument =
                        item.status === DocumentStatusEnum.Error ||
                        item.status === DocumentStatusEnum.Rejected ||
                        !!item.errorCode ||
                        !item.isProcessingEndStateReached;

                    return (
                        <div
                            key={item.id}
                            onClick={(e) => onDocumentClick(item.id, e)}
                            className={cx(
                                "relative flex cursor-pointer items-center gap-3 border-b border-secondary px-4 py-3 outline-none select-none",
                                selectedDocumentId === item.id ? "border-l-2 border-l-blue-500 bg-gray-200" : "hover:bg-secondary/50",
                            )}
                        >
                            {/* Checkbox - centered vertically within the list item (hidden for error documents) */}
                            {isErrorDocument ? (
                                // Placeholder to maintain spacing alignment
                                <div className="w-4 flex-shrink-0" />
                            ) : (
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDocumentCheckboxChange(item.id, !selectedDocumentIds.has(item.id));
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className="flex flex-shrink-0 items-center"
                                >
                                    <Checkbox
                                        size="sm"
                                        isSelected={selectedDocumentIds.has(item.id)}
                                        onChange={(checked) => {
                                            onDocumentCheckboxChange(item.id, checked);
                                        }}
                                    />
                                </div>
                            )}

                            {/* Content - two lines */}
                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                {/* Line 1: File Name */}
                                <span className="truncate text-sm font-medium text-primary">
                                    {item.generatedName || item.fileName || `Document #${item.id}`}
                                </span>

                                {/* Line 2: Date • Document Type • Badge */}
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex min-w-0 flex-1 items-center gap-2">
                                        <span className="text-xs text-tertiary">{formatDate(item.createdAt)}</span>
                                        {item.documentType && (
                                            <>
                                                <span className="text-xs text-quaternary">•</span>
                                                <span className="truncate text-xs text-tertiary">{item.documentType}</span>
                                            </>
                                        )}
                                    </div>

                                    <div className="flex flex-shrink-0 items-center gap-2">
                                        <Badge size="sm" color={getStatusColor(item.status, item.errorCode, item.hasRetryWarning)} type="color">
                                            {getStatusLabel(item.status, item.errorCode, item.hasRetryWarning)}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Empty State */}
                {filteredDocuments.length === 0 && (
                    <div className="flex flex-col items-center justify-center px-4 py-12">
                        <FeaturedIcon icon={SearchLg} color="gray" theme="light" size="md" />
                        <h3 className="mt-3 mb-1 text-sm font-medium text-secondary">No documents found</h3>
                        <p className="text-center text-xs text-tertiary">
                            {filter !== "all" ? "No documents match the current filter" : "Upload documents from the Home page to get started"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// PDF VIEWER PANEL
// =============================================================================

interface PDFViewerPanelProps {
    document: DocumentDetailDto | null;
    filesApi: ReturnType<typeof useFilesApi>;
    isListCollapsed?: boolean;
    onExpandList?: () => void;
}

function PDFViewerPanel({ document, filesApi, isListCollapsed, onExpandList }: PDFViewerPanelProps) {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Ref to track current URL for proper cleanup (avoids stale closure)
    const pdfUrlRef = useRef<string | null>(null);

    // Fetch PDF content when document changes
    useEffect(() => {
        // Cleanup previous URL using ref (not stale state)
        if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
            setPdfUrl(null);
        }

        if (!document?.fileStorage?.id) {
            setIsLoading(false);
            setError(null);
            return;
        }

        const fetchPdfContent = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Call GetFileContent API
                const fileContent = await filesApi.getFileContent(document.fileStorage.id);

                // Convert base64 content to byte array
                const binaryString = atob(fileContent.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                // Check if we need to extract specific pages
                const hasPageRange = document.pages?.start != null && document.pages?.end != null;

                let url: string;
                if (hasPageRange) {
                    // Use pdf-lib to extract only the specified pages
                    const pdfDoc = await PDFDocument.load(bytes);
                    const newPdfDoc = await PDFDocument.create();

                    // Extract pages (PDF pages are 0-indexed, but our API uses 1-indexed)
                    const startPage = document.pages.start;
                    const endPage = document.pages.end;

                    // Copy each page in the range
                    const pagesToCopy = [];
                    for (let i = startPage; i <= endPage; i++) {
                        pagesToCopy.push(i - 1); // pdf-lib uses 0-based indexing
                    }

                    const copiedPages = await newPdfDoc.copyPages(pdfDoc, pagesToCopy);
                    copiedPages.forEach((page) => {
                        newPdfDoc.addPage(page);
                    });

                    // Save the new PDF - pdf-lib returns Uint8Array
                    const pdfBytes = await newPdfDoc.save();
                    // Create blob from the PDF bytes
                    // Note: pdf-lib save() returns Uint8Array backed by ArrayBuffer (not SharedArrayBuffer)
                    const pdfArrayBuffer = pdfBytes.buffer.slice(pdfBytes.byteOffset, pdfBytes.byteOffset + pdfBytes.byteLength) as ArrayBuffer;
                    const blob = new Blob([pdfArrayBuffer], { type: "application/pdf" });
                    url = URL.createObjectURL(blob);
                } else {
                    // Force application/pdf type to prevent Chrome from treating it as a download,
                    // which can trigger the Windows blocked popup/download "ding" sound
                    const blob = new Blob([bytes], { type: "application/pdf" });
                    url = URL.createObjectURL(blob);
                }

                pdfUrlRef.current = url;
                setPdfUrl(url);
            } catch (err) {
                console.error("Failed to fetch PDF content:", err);
                setError(err instanceof Error ? err.message : "Failed to load PDF");
            } finally {
                setIsLoading(false);
            }
        };

        fetchPdfContent();

        // Cleanup on unmount or document change using ref
        return () => {
            if (pdfUrlRef.current) {
                URL.revokeObjectURL(pdfUrlRef.current);
                pdfUrlRef.current = null;
            }
        };
    }, [document?.fileStorage?.id, document?.pages?.start, document?.pages?.end, filesApi]);

    // Expand bar shown when left panel is collapsed
    const expandBar = isListCollapsed ? (
        <div className="flex-shrink-0 border-b border-secondary bg-primary px-4 py-2">
            <ButtonUtility icon={LayoutLeft} size="sm" color="secondary" tooltip="Expand panel" onClick={onExpandList} />
        </div>
    ) : null;

    if (!document) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={File06} color="gray" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>No Document Selected</EmptyState.Title>
                            <EmptyState.Description>Select a document from the left sidebar to view it here</EmptyState.Description>
                        </EmptyState.Content>
                    </EmptyState>
                </div>
            </div>
        );
    }

    if (!document.fileStorage?.id) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={File06} color="gray" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>No Preview Available</EmptyState.Title>
                            <EmptyState.Description>{document.fileName || `Document #${document.id}`}</EmptyState.Description>
                            <EmptyState.Description>This document does not have an attached file</EmptyState.Description>
                        </EmptyState.Content>
                    </EmptyState>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <LoadingIndicator type="line-simple" size="md" label="Loading PDF..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={AlertCircle} color="error" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>Failed to Load PDF</EmptyState.Title>
                            <EmptyState.Description>{error}</EmptyState.Description>
                        </EmptyState.Content>
                        <EmptyState.Footer>
                            <Button color="primary" onClick={() => window.location.reload()}>
                                Retry
                            </Button>
                        </EmptyState.Footer>
                    </EmptyState>
                </div>
            </div>
        );
    }

    if (!pdfUrl) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={File06} color="gray" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>No Preview Available</EmptyState.Title>
                            <EmptyState.Description>{document.fileName || `Document #${document.id}`}</EmptyState.Description>
                        </EmptyState.Content>
                    </EmptyState>
                </div>
            </div>
        );
    }

    // Check if document has specific page range
    const hasPageRange = document.pages && document.pages.start && document.pages.end;
    const isSinglePage = hasPageRange && document.pages.start === document.pages.end;

    return (
        <div className="flex h-full flex-col bg-secondary">
            {/* Page Range Header (or expand bar when collapsed without page range) */}
            {hasPageRange ? (
                <div className="flex-shrink-0 border-b border-secondary bg-primary px-4 py-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {isListCollapsed && <ButtonUtility icon={LayoutLeft} size="sm" color="secondary" tooltip="Expand panel" onClick={onExpandList} />}
                            <span className="text-sm font-medium text-secondary">
                                {isSinglePage ? `Page ${document.pages.start}` : `Pages ${document.pages.start} - ${document.pages.end}`}
                            </span>
                            {document.pages.total && <span className="text-xs text-tertiary">of {document.pages.total} total</span>}
                        </div>
                        <Badge size="sm" color="gray" type="modern">
                            Document Section
                        </Badge>
                    </div>
                </div>
            ) : (
                expandBar
            )}

            <div className="flex-1 overflow-hidden">
                <object data={pdfUrl} type="application/pdf" className="h-full w-full">
                    {/* Fallback if object tag fails */}
                    <div className="flex h-full flex-col items-center justify-center p-8">
                        <FeaturedIcon icon={File06} color="gray" theme="light" size="lg" />
                        <h3 className="mt-4 mb-2 text-lg font-medium text-primary">PDF Preview</h3>
                        <p className="mb-4 text-tertiary">{document.fileName}</p>
                        {hasPageRange && (
                            <p className="mb-4 text-sm text-quaternary">
                                {isSinglePage ? `Showing page ${document.pages.start}` : `Showing pages ${document.pages.start} - ${document.pages.end}`}
                            </p>
                        )}
                        <div className="flex gap-2">
                            <Button color="primary" onClick={() => window.open(pdfUrl, "_blank")}>
                                Open PDF
                            </Button>
                        </div>
                    </div>
                </object>
            </div>
        </div>
    );
}

// =============================================================================
// DOCUMENT DETAILS PANEL
// =============================================================================

interface DocumentDetailsPanelProps {
    document: DocumentDetailDto | null;
    onExport?: (document: DocumentDetailDto) => void;
    onExternalExport?: (document: DocumentDetailDto) => void;
    onDelete?: (document: DocumentDetailDto) => void;
    onDocumentUpdate?: (documentId: number) => Promise<void>;
    isExporting?: boolean;
    isExternalExporting?: boolean;
    hasExported?: boolean;
    hasExternalExportEntitlement?: boolean;
    routingCodes?: RoutingCodeDto[];
    onHasChangesChange?: (hasChanges: boolean) => void;
    exportError?: string | null;
    fieldDefinitionsCache: Map<number, CustomFieldDefinitionDto[]>;
    onFieldDefinitionsCacheUpdate: (docTypeId: number, definitions: CustomFieldDefinitionDto[]) => void;
}

function DocumentDetailsPanel({
    document,
    onExport,
    onExternalExport,
    onDelete,
    onDocumentUpdate,
    isExporting = false,
    isExternalExporting = false,
    hasExported = false,
    hasExternalExportEntitlement = false,
    routingCodes,
    onHasChangesChange,
    exportError,
    fieldDefinitionsCache,
    onFieldDefinitionsCacheUpdate,
}: DocumentDetailsPanelProps) {
    const [activeTab, setActiveTab] = useState("extracted");
    const contentAreaRef = useRef<HTMLDivElement>(null);
    const documentsApi = useDocumentsApi();

    // State for tracking edited field values
    // Map: customFieldDefinitionId -> { fieldType, value }
    const [editedFieldValues, setEditedFieldValues] = useState<Map<number, { fieldType: string; value: string | number | boolean | DateValue | null }>>(
        new Map(),
    );
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState<string | null>(null);

    const [loadingDefinitions, setLoadingDefinitions] = useState(false);

    // Current field definitions for the selected document
    const [currentFieldDefinitions, setCurrentFieldDefinitions] = useState<CustomFieldDefinitionDto[]>([]);

    // State for tracking validation errors (required fields without values)
    const [validationErrors, setValidationErrors] = useState<Set<number>>(new Set());

    const tabs = [
        { id: "extracted", label: "Fields" },
        { id: "info", label: "Info" },
    ];

    // Date conversion helpers
    const isoStringToDateValue = useCallback((isoString: string | null | undefined): DateValue | null => {
        if (!isoString) return null;
        try {
            // Extract date part (YYYY-MM-DD) from ISO string
            const datePart = isoString.split("T")[0];
            return parseDate(datePart);
        } catch {
            return null;
        }
    }, []);

    // Convert DateValue to ISO string format exactly as selected (no timezone conversion)
    // Format: YYYY-MM-DDTHH:mm:ssZ (using midnight UTC to preserve the selected date)
    const dateValueToIsoString = useCallback((dateValue: DateValue | null | unknown): string | null => {
        if (!dateValue) return null;
        // Type guard: check if it's a DateValue by checking for toDate method
        if (typeof dateValue === "object" && dateValue !== null && "toDate" in dateValue && typeof (dateValue as DateValue).toDate === "function") {
            try {
                const dateVal = dateValue as DateValue;
                // Format as YYYY-MM-DDTHH:mm:ssZ using the selected date at midnight UTC
                // This preserves the exact date selected regardless of timezone
                const year = dateVal.year;
                const month = String(dateVal.month).padStart(2, "0");
                const day = String(dateVal.day).padStart(2, "0");
                return `${year}-${month}-${day}T00:00:00Z`;
            } catch {
                return null;
            }
        }
        return null;
    }, []);

    // Initialize field values from document and definitions
    const initializeFieldValues = useCallback(
        (doc: DocumentDetailDto, definitions: CustomFieldDefinitionDto[]) => {
            const initialValues = new Map<number, { fieldType: string; value: string | number | boolean | DateValue | null }>();

            // Create a map of existing values by definition ID
            const valueMap = new Map<number, CustomFieldValueDto>();
            if (doc.customFieldValues) {
                doc.customFieldValues.forEach((fieldValue) => {
                    valueMap.set(fieldValue.customFieldDefinitionId, fieldValue);
                });
            }

            // Initialize values for all definitions
            definitions.forEach((definition) => {
                const fieldValue = valueMap.get(definition.id);
                const fieldType = definition.fieldType;

                if (fieldValue) {
                    // Field has a value - use it
                    const currentValue = getCustomFieldDtoDisplayValue(fieldValue);

                    // Convert date strings to DateValue for DatePicker
                    if (fieldType === FieldTypeEnum.Date || fieldType === "Date") {
                        initialValues.set(definition.id, {
                            fieldType,
                            value: isoStringToDateValue(currentValue as string | null),
                        });
                    } else {
                        initialValues.set(definition.id, {
                            fieldType,
                            value: currentValue,
                        });
                    }
                } else {
                    // Field has no value - set to null
                    initialValues.set(definition.id, {
                        fieldType,
                        value: null,
                    });
                }
            });

            setEditedFieldValues(initialValues);
        },
        [isoStringToDateValue],
    );

    // Fetch custom field definitions when document changes
    useEffect(() => {
        if (!document?.documentType?.id) {
            setCurrentFieldDefinitions([]);
            setEditedFieldValues(new Map());
            return;
        }

        const documentTypeId = document.documentType.id;

        // Check cache first
        const cached = fieldDefinitionsCache.get(documentTypeId);
        if (cached) {
            setCurrentFieldDefinitions(cached);
            // Initialize values after definitions are set
            initializeFieldValues(document, cached);
            return;
        }

        // Fetch definitions
        const fetchDefinitions = async () => {
            try {
                setLoadingDefinitions(true);
                const response = await documentsApi.getCustomFieldDefinitions(documentTypeId);

                // Cache the definitions via parent callback
                onFieldDefinitionsCacheUpdate(documentTypeId, response.customFieldDefinitions);

                setCurrentFieldDefinitions(response.customFieldDefinitions);
                // Initialize values after definitions are fetched
                initializeFieldValues(document, response.customFieldDefinitions);
            } catch (error) {
                console.error("Failed to fetch custom field definitions:", error);
                setCurrentFieldDefinitions([]);
            } finally {
                setLoadingDefinitions(false);
            }
        };

        fetchDefinitions();
    }, [document?.id, document?.documentType?.id, document, documentsApi, fieldDefinitionsCache, initializeFieldValues, onFieldDefinitionsCacheUpdate]);

    // Check if document can be exported (Parsed or Exported status, no error)
    const isExportEligible =
        !document?.errorCode &&
        document?.isProcessingEndStateReached &&
        (document?.status === DocumentStatusEnum.Parsed || document?.status === DocumentStatusEnum.Exported);

    const canDelete =
        document?.status === DocumentStatusEnum.Pending ||
        document?.status === DocumentStatusEnum.Error ||
        document?.status === DocumentStatusEnum.Rejected ||
        !!document?.errorCode;

    // Check if there are changes
    const hasChanges = useMemo(() => {
        if (!document || currentFieldDefinitions.length === 0) return false;

        // Create a map of original values by definition ID
        const originalValueMap = new Map<number, string | number | boolean | null>();
        if (document.customFieldValues) {
            document.customFieldValues.forEach((fieldValue) => {
                const value = getCustomFieldDtoDisplayValue(fieldValue);
                originalValueMap.set(fieldValue.customFieldDefinitionId, value);
            });
        }

        // Check all field definitions for changes
        for (const definition of currentFieldDefinitions) {
            const editedValue = editedFieldValues.get(definition.id);
            if (!editedValue) continue;

            const originalValue = originalValueMap.get(definition.id) ?? null;
            const fieldType = definition.fieldType;

            // Compare values based on type
            if (fieldType === FieldTypeEnum.Date || fieldType === "Date") {
                const originalDateValue = isoStringToDateValue(originalValue as string | null);
                const editedValueRaw = editedValue.value;

                // Ensure edited value is a DateValue object
                const editedDateValue =
                    editedValueRaw && typeof editedValueRaw === "object" && "toDate" in editedValueRaw ? (editedValueRaw as DateValue) : null;

                if (originalDateValue === null && editedDateValue === null) continue;
                if (originalDateValue === null || editedDateValue === null) return true;

                // Compare date values
                if (
                    originalDateValue.year !== editedDateValue.year ||
                    originalDateValue.month !== editedDateValue.month ||
                    originalDateValue.day !== editedDateValue.day
                ) {
                    return true;
                }
            } else {
                // For other types, simple comparison (including null checks)
                if (originalValue !== editedValue.value) {
                    return true;
                }
            }
        }

        return false;
    }, [document, currentFieldDefinitions, editedFieldValues, isoStringToDateValue]);

    // Notify parent when hasChanges changes
    useEffect(() => {
        onHasChangesChange?.(hasChanges);
    }, [hasChanges, onHasChangesChange]);

    // Check if a value is empty (for validation)
    const isEmptyValue = useCallback((value: string | number | boolean | DateValue | null | undefined): boolean => {
        if (value === null || value === undefined) return true;
        if (typeof value === "string") return value.trim() === "";
        if (typeof value === "number") return isNaN(value);
        if (typeof value === "boolean") return false; // Boolean false is a valid value
        // DateValue - check if it's a valid date object
        if (typeof value === "object" && "toDate" in value) return false; // DateValue is valid
        return true;
    }, []);

    // Validate required fields
    const validateRequiredFields = useCallback((): Set<number> => {
        const errors = new Set<number>();

        if (!document) return errors;

        for (const definition of currentFieldDefinitions) {
            const editedValue = editedFieldValues.get(definition.id);
            const userHasEdited = editedValue !== undefined;
            const originalFieldValue = document.customFieldValues?.find((fv) => fv.customFieldDefinitionId === definition.id);

            // Required field validation
            if (definition.isRequired) {
                // Check edited value first, then fall back to original value
                let value: string | number | boolean | DateValue | null | undefined = editedValue?.value;

                // If no edited value, check original value from document
                if (value === undefined) {
                    if (originalFieldValue) {
                        value = getCustomFieldDtoDisplayValue(originalFieldValue);
                        // Convert date strings to DateValue for validation
                        if ((definition.fieldType === FieldTypeEnum.Date || definition.fieldType === "Date") && typeof value === "string") {
                            value = isoStringToDateValue(value);
                        }
                    } else {
                        value = null;
                    }
                }

                if (isEmptyValue(value)) {
                    errors.add(definition.id);
                    continue;
                }
            }

            // Server-side validation error: show if containsError is true AND user hasn't edited the field
            // (if user edited the field, we optimistically clear the error state)
            if (originalFieldValue?.containsError && !userHasEdited) {
                errors.add(definition.id);
            }
        }

        return errors;
    }, [currentFieldDefinitions, editedFieldValues, isEmptyValue, document, isoStringToDateValue]);

    // Check if any required fields are missing saved values or have validation errors (only checks persisted data, not unsaved edits)
    const hasSavedRequiredFieldErrors = useMemo(() => {
        if (!document || currentFieldDefinitions.length === 0) return false;

        for (const definition of currentFieldDefinitions) {
            const originalFieldValue = document.customFieldValues?.find((fv) => fv.customFieldDefinitionId === definition.id);

            // Check for containsError flag on any field (validation failed on server)
            if (originalFieldValue?.containsError) return true;

            // Check for required field without value
            if (definition.isRequired) {
                if (!originalFieldValue) return true;

                const value = getCustomFieldDtoDisplayValue(originalFieldValue);
                if (isEmptyValue(value)) return true;
            }
        }
        return false;
    }, [currentFieldDefinitions, document, isEmptyValue]);

    // Full export readiness: status eligible, all required fields saved, no unsaved changes, definitions loaded
    const canExport = isExportEligible && !hasSavedRequiredFieldErrors && !hasChanges && !loadingDefinitions;

    // Handle field value change
    const handleFieldChange = useCallback((customFieldDefinitionId: number, fieldType: string, value: string | number | boolean | DateValue | null) => {
        setEditedFieldValues((prev) => {
            const newMap = new Map(prev);
            newMap.set(customFieldDefinitionId, { fieldType, value });
            return newMap;
        });
    }, []);

    // Handle update
    const handleUpdate = useCallback(async () => {
        if (!document || !hasChanges) return;

        // Reset validation errors and re-validate before saving
        setValidationErrors(new Set());
        const errors = validateRequiredFields();
        if (errors.size > 0) {
            setValidationErrors(errors);
            // Scroll to first error field
            const firstErrorId = Array.from(errors)[0];
            setTimeout(() => {
                const errorElement = window.document.getElementById(`field-${firstErrorId}`);
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
                    // Try to focus the input inside the field container
                    const input = errorElement.querySelector("input, button");
                    if (input && input instanceof HTMLElement) {
                        input.focus();
                    }
                }
            }, 100);
            return;
        }

        try {
            setIsUpdating(true);

            // Build request DTO - only include fields that have changed
            const customFieldValues: UpdateCustomFieldValueDto[] = [];

            // Get original field values from document
            const originalValueMap = new Map<number, string | number | boolean | null>();
            if (document.customFieldValues) {
                document.customFieldValues.forEach((fieldValue) => {
                    const value = getCustomFieldDtoDisplayValue(fieldValue);
                    originalValueMap.set(fieldValue.customFieldDefinitionId, value);
                });
            }

            // Check all field definitions for changes
            for (const definition of currentFieldDefinitions) {
                const editedValue = editedFieldValues.get(definition.id);
                if (!editedValue) continue;

                const originalValue = originalValueMap.get(definition.id) ?? null;
                const fieldType = definition.fieldType;

                // Check if the value has actually changed
                let hasChanged = false;

                if (fieldType === FieldTypeEnum.Date || fieldType === "Date") {
                    // Compare dates
                    const originalDateValue = isoStringToDateValue(originalValue as string | null);
                    const editedDateValue =
                        editedValue.value && typeof editedValue.value === "object" && "toDate" in editedValue.value ? (editedValue.value as DateValue) : null;

                    if (originalDateValue === null && editedDateValue === null) {
                        hasChanged = false;
                    } else if (originalDateValue === null || editedDateValue === null) {
                        hasChanged = true;
                    } else {
                        hasChanged =
                            originalDateValue.year !== editedDateValue.year ||
                            originalDateValue.month !== editedDateValue.month ||
                            originalDateValue.day !== editedDateValue.day;
                    }
                } else {
                    // For other types, simple comparison
                    hasChanged = originalValue !== editedValue.value;
                }

                // Only include changed fields
                if (!hasChanged) {
                    continue;
                }

                let valueString: string | null = null;

                // Convert value to string based on type
                switch (fieldType) {
                    case FieldTypeEnum.String:
                    case "String":
                        valueString = editedValue.value !== null && editedValue.value !== undefined ? String(editedValue.value) : null;
                        break;
                    case FieldTypeEnum.Integer:
                    case "Integer":
                        valueString = editedValue.value !== null && editedValue.value !== undefined ? String(editedValue.value) : null;
                        break;
                    case FieldTypeEnum.Decimal:
                    case "Decimal":
                        valueString = editedValue.value !== null && editedValue.value !== undefined ? String(editedValue.value) : null;
                        break;
                    case FieldTypeEnum.Date:
                    case "Date":
                        // Convert DateValue to ISO string format (YYYY-MM-DDTHH:mm:ssZ)
                        // Preserves the exact date selected regardless of timezone
                        if (editedValue.value && typeof editedValue.value === "object" && "toDate" in editedValue.value) {
                            valueString = dateValueToIsoString(editedValue.value);
                        } else {
                            valueString = null;
                        }
                        break;
                    case FieldTypeEnum.Boolean:
                    case "Boolean":
                        // Convert boolean to string: "true" or "false"
                        if (editedValue.value === true) {
                            valueString = "true";
                        } else if (editedValue.value === false) {
                            valueString = "false";
                        } else {
                            valueString = null;
                        }
                        break;
                }

                const dto: UpdateCustomFieldValueDto = {
                    customFieldDefinitionId: definition.id,
                    value: valueString,
                };

                customFieldValues.push(dto);
            }

            // Don't send if no fields changed
            if (customFieldValues.length === 0) {
                console.warn("No fields changed, skipping update");
                setIsUpdating(false);
                return;
            }

            console.log("Updating custom fields:", customFieldValues);
            const requestDto: UpdateCustomFieldsRequestDto = { customFieldValues };

            // Call API
            const response = await documentsApi.updateCustomFields(document.id, requestDto);
            console.log("Update response:", response);

            // Refresh document
            if (onDocumentUpdate) {
                await onDocumentUpdate(document.id);
            }

            // Clear edited values after successful update
            setEditedFieldValues(new Map());

            // Reset validation errors after successful save
            setValidationErrors(new Set());
        } catch (error) {
            console.error("Failed to update custom fields:", error);
            setUpdateError("Failed to update fields. Please try again.");
        } finally {
            setIsUpdating(false);
        }
    }, [
        document,
        hasChanges,
        editedFieldValues,
        currentFieldDefinitions,
        documentsApi,
        onDocumentUpdate,
        dateValueToIsoString,
        isoStringToDateValue,
        validateRequiredFields,
    ]);

    // Handle clear field
    const handleClearField = useCallback(
        (customFieldDefinitionId: number, fieldType: string) => {
            handleFieldChange(customFieldDefinitionId, fieldType, null);
        },
        [handleFieldChange],
    );

    if (!document) {
        return (
            <div className="border-l border-secondary bg-primary" style={{ width: "384px", minWidth: "384px", maxWidth: "384px" }}>
                <div className="flex flex-col items-center justify-center px-6 py-12">
                    <FeaturedIcon icon={File06} color="gray" theme="light" size="lg" />
                    <h3 className="mt-4 mb-2 text-lg font-medium text-primary">No Document Selected</h3>
                    <p className="text-center text-tertiary">Select a document to view details</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex h-full flex-col overflow-hidden border-l border-secondary bg-primary"
            style={{ width: "384px", minWidth: "384px", maxWidth: "384px" }}
        >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-secondary px-[18px] pt-[18px] pb-[18px]">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-primary">Details</h2>
                    <Badge size="sm" color={getStatusColor(document.status, document.errorCode, document.hasRetryWarning)} type="color">
                        {getStatusLabel(document.status, document.errorCode, document.hasRetryWarning)}
                    </Badge>
                </div>

                {/* Retry warning indicator */}
                {document.hasRetryWarning && (
                    <div className="border-warning mb-3 rounded-lg border bg-warning-25 p-3 text-warning-700">
                        <div className="mb-2 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-warning-primary" />
                            <span className="text-sm font-medium">Processing Paused</span>
                        </div>
                        <p className="text-xs">A transient error occurred. This job will be retried soon.</p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                    {isExportEligible && (
                        <>
                            <Tooltip title="All required fields must be filled before exporting" isDisabled={canExport}>
                                <Button
                                    size="sm"
                                    color="primary"
                                    className="flex-1"
                                    iconLeading={FileCheck02}
                                    onClick={() => onExport?.(document)}
                                    isDisabled={!canExport || isExporting}
                                    isLoading={isExporting}
                                >
                                    {hasExported || document.status === DocumentStatusEnum.Exported ? "CSV Export Again" : "CSV Export"}
                                </Button>
                            </Tooltip>
                            {hasExternalExportEntitlement && (
                                <Tooltip title="All required fields must be filled before exporting" isDisabled={canExport}>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        iconLeading={FileCheck02}
                                        onClick={() => onExternalExport?.(document)}
                                        isDisabled={!canExport || isExternalExporting}
                                        isLoading={isExternalExporting}
                                    >
                                        External Export
                                    </Button>
                                </Tooltip>
                            )}
                        </>
                    )}
                    {canDelete && (
                        <Button size="sm" color="secondary-destructive" iconLeading={Trash01} onClick={() => onDelete?.(document)}>
                            Delete
                        </Button>
                    )}
                </div>

                {/* Export Error */}
                {exportError && <p className="mt-2 text-sm text-error-primary">{exportError}</p>}
            </div>

            {/* Tabs */}
            <Tabs
                key={`tabs-${document.id}`}
                defaultSelectedKey="extracted"
                selectedKey={activeTab}
                onSelectionChange={(key) => setActiveTab(key as string)}
                className="flex flex-1 flex-col overflow-hidden"
            >
                <div className="flex-shrink-0 border-b border-secondary">
                    <TabList items={tabs} type="underline" size="sm" className="w-full justify-evenly px-[18px] pt-2">
                        {(item) => <Tab key={item.id} id={item.id} label={item.label} />}
                    </TabList>
                </div>

                {/* Content */}
                <div ref={contentAreaRef} className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
                    <TabPanel id="extracted" className="flex flex-1 flex-col overflow-hidden">
                        {/* Scrollable Fields Section */}
                        <div className="flex-1 overflow-y-auto" style={{ padding: "18px" }}>
                            {/* Custom Field Definitions */}
                            {loadingDefinitions ? (
                                <div className="py-8 text-center text-tertiary">
                                    <p>Loading fields...</p>
                                </div>
                            ) : currentFieldDefinitions.length > 0 ? (
                                <div className="space-y-3">
                                    {currentFieldDefinitions
                                        .filter((def) => def.isVisible !== false)
                                        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                                        .map((definition) => {
                                            const label = definition.fieldLabel || `Field ${definition.id}`;
                                            const fieldType = definition.fieldType;
                                            const editedValue = editedFieldValues.get(definition.id);
                                            const currentValue = editedValue?.value ?? null;
                                            const hasValue = currentValue !== null && currentValue !== undefined;
                                            const hasError = validationErrors.has(definition.id);

                                            return (
                                                <div key={definition.id} id={`field-${definition.id}`} className="relative">
                                                    <label className="mb-1 block text-xs font-medium text-tertiary">
                                                        {label}
                                                        {definition.isRequired && <span className="ml-1 text-error-primary">*</span>}
                                                    </label>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1">
                                                            {definition.fieldCode === "routing_code" && routingCodes && routingCodes.length > 0 ? (
                                                                <Select
                                                                    selectedKey={(currentValue as string) ?? null}
                                                                    onSelectionChange={(key) => handleFieldChange(definition.id, fieldType, key as string)}
                                                                    size="sm"
                                                                    placeholder="Select routing code"
                                                                    items={routingCodes.map((rc) => ({ id: rc.code, label: rc.code }))}
                                                                >
                                                                    {(item) => <Select.Item id={item.id} label={item.label} />}
                                                                </Select>
                                                            ) : fieldType === FieldTypeEnum.String || fieldType === "String" ? (
                                                                <Input
                                                                    value={(currentValue as string) ?? ""}
                                                                    onChange={(value) => handleFieldChange(definition.id, fieldType, value)}
                                                                    size="sm"
                                                                    placeholder={`Enter ${label.toLowerCase()}`}
                                                                    isInvalid={hasError}
                                                                />
                                                            ) : fieldType === FieldTypeEnum.Integer || fieldType === "Integer" ? (
                                                                <Input
                                                                    type="number"
                                                                    value={currentValue !== null && currentValue !== undefined ? String(currentValue) : ""}
                                                                    onChange={(value) => {
                                                                        // Only allow integers (no decimals)
                                                                        const cleanValue = value.replace(/[^0-9-]/g, "");
                                                                        const numValue = cleanValue === "" ? null : parseInt(cleanValue, 10);
                                                                        if (numValue !== null && !isNaN(numValue)) {
                                                                            handleFieldChange(definition.id, fieldType, numValue);
                                                                        } else if (numValue === null) {
                                                                            handleFieldChange(definition.id, fieldType, null);
                                                                        }
                                                                    }}
                                                                    size="sm"
                                                                    placeholder={`Enter ${label.toLowerCase()}`}
                                                                    isInvalid={hasError}
                                                                />
                                                            ) : fieldType === FieldTypeEnum.Decimal || fieldType === "Decimal" ? (
                                                                <Input
                                                                    type="number"
                                                                    value={currentValue !== null && currentValue !== undefined ? String(currentValue) : ""}
                                                                    onChange={(value) => {
                                                                        const numValue = value === "" ? null : parseFloat(value);
                                                                        if (numValue !== null && !isNaN(numValue)) {
                                                                            handleFieldChange(definition.id, fieldType, numValue);
                                                                        } else if (numValue === null) {
                                                                            handleFieldChange(definition.id, fieldType, null);
                                                                        }
                                                                    }}
                                                                    size="sm"
                                                                    placeholder={`Enter ${label.toLowerCase()}`}
                                                                    isInvalid={hasError}
                                                                />
                                                            ) : fieldType === FieldTypeEnum.Boolean || fieldType === "Boolean" ? (
                                                                <div className={cx("flex items-center", hasError && "border-error-primary rounded border p-1")}>
                                                                    <Checkbox
                                                                        isSelected={currentValue === true}
                                                                        onChange={(isSelected) => handleFieldChange(definition.id, fieldType, isSelected)}
                                                                        size="sm"
                                                                    />
                                                                    <span className="ml-2 text-sm text-primary">{currentValue === true ? "Yes" : "No"}</span>
                                                                </div>
                                                            ) : fieldType === FieldTypeEnum.Date || fieldType === "Date" ? (
                                                                <DatePicker
                                                                    value={
                                                                        currentValue && typeof currentValue === "object" && "toDate" in currentValue
                                                                            ? (currentValue as DateValue)
                                                                            : null
                                                                    }
                                                                    onChange={(value) => handleFieldChange(definition.id, fieldType, value)}
                                                                    isInvalid={hasError}
                                                                />
                                                            ) : (
                                                                <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">
                                                                    {formatFieldValue(
                                                                        currentValue && typeof currentValue === "object" && "toDate" in currentValue
                                                                            ? (dateValueToIsoString(currentValue) ?? null)
                                                                            : (currentValue as string | number | boolean | null),
                                                                        fieldType,
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {hasValue && (
                                                            <ButtonUtility
                                                                icon={XClose}
                                                                size="xs"
                                                                color="tertiary"
                                                                tooltip={`Clear ${label}`}
                                                                onClick={() => handleClearField(definition.id, fieldType)}
                                                            />
                                                        )}
                                                    </div>
                                                    {definition.helpText && <p className="mt-1 text-xs text-quaternary">{definition.helpText}</p>}
                                                </div>
                                            );
                                        })}
                                </div>
                            ) : (
                                <div className="py-8 text-center text-tertiary">
                                    <p>No fields available for this document type</p>
                                </div>
                            )}
                        </div>

                        {/* Fixed Update Button */}
                        {(hasChanges || updateError) && (
                            <div className="flex-shrink-0 border-t border-secondary p-[18px]">
                                {updateError && <p className="mb-2 text-sm text-error-primary">{updateError}</p>}
                                {hasChanges && (
                                    <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() => {
                                            setUpdateError(null);
                                            handleUpdate();
                                        }}
                                        isDisabled={isUpdating}
                                        isLoading={isUpdating}
                                        className="w-full"
                                    >
                                        Update Fields
                                    </Button>
                                )}
                            </div>
                        )}
                    </TabPanel>

                    <TabPanel id="info" className="flex-1 overflow-y-auto" style={{ padding: "18px" }}>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-1 block text-xs font-medium text-tertiary">Document ID</label>
                                <div className="rounded bg-secondary px-3 py-2 font-mono text-sm text-primary">{document.id}</div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-tertiary">Document Name</label>
                                <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{document.generatedName || document.fileName || "-"}</div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-tertiary">Original File</label>
                                <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{document.fileName || "-"}</div>
                            </div>

                            {document.documentType && (
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-tertiary">Document Type</label>
                                    <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">
                                        {document.documentType.name || document.documentType.code || "-"}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="mb-1 block text-xs font-medium text-tertiary">Status</label>
                                <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{getStatusLabel(document.status, document.errorCode)}</div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-tertiary">Created</label>
                                <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{formatDateTime(document.createdAt)}</div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-tertiary">Updated</label>
                                <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{formatDateTime(document.updatedAt)}</div>
                            </div>

                            {document.pages && (
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-tertiary">Pages</label>
                                    <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">
                                        {document.pages.start} - {document.pages.end} of {document.pages.total}
                                    </div>
                                </div>
                            )}

                            {document.extractionVersion && (
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-tertiary">Extraction Version</label>
                                    <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">
                                        v{document.extractionVersion.version}
                                        {document.extractionVersion.confidence !== undefined &&
                                            ` (${(document.extractionVersion.confidence * 100).toFixed(0)}% confidence)`}
                                    </div>
                                </div>
                            )}

                            {document.organizationName && (
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-tertiary">Organization</label>
                                    <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{document.organizationName}</div>
                                </div>
                            )}
                        </div>
                    </TabPanel>
                </div>
            </Tabs>
        </div>
    );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function LegacyDocumentsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const documentsApi = useDocumentsApi();
    const filesApi = useFilesApi();

    // State
    const [documents, setDocuments] = useState<DocumentListItemDto[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<DocumentDetailDto | null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [_isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<StatusFilter>("all");
    const [isExporting, setIsExporting] = useState(false);
    const [hasExported, setHasExported] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(new Set());
    const [isMultiExporting, setIsMultiExporting] = useState(false);
    const [isExternalExporting, setIsExternalExporting] = useState(false);
    const [isMultiExternalExporting, setIsMultiExternalExporting] = useState(false);
    const [selectionAnchorId, setSelectionAnchorId] = useState<number | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [pendingDocumentId, setPendingDocumentId] = useState<number | null>(null);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<DocumentDetailDto | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isListCollapsed, setIsListCollapsed] = useState(false);
    const [fieldDefinitionsCache, setFieldDefinitionsCache] = useState<Map<number, CustomFieldDefinitionDto[]>>(new Map());
    const [multiExportError, setMultiExportError] = useState<string | null>(null);

    // Stable callback for updating field definitions cache (avoids re-triggering child effects)
    const handleFieldDefinitionsCacheUpdate = useCallback((docTypeId: number, definitions: CustomFieldDefinitionDto[]) => {
        setFieldDefinitionsCache((prev) => {
            const newCache = new Map(prev);
            newCache.set(docTypeId, definitions);
            return newCache;
        });
    }, []);

    // Get selected document ID from URL
    const selectedDocumentId = searchParams.get("id");
    const selectedDocumentIdNum = selectedDocumentId ? parseInt(selectedDocumentId, 10) : null;

    // Client id for GET /clients/{clientId}/entitlements (from document list)
    const clientId = documents.find((d) => d.id === selectedDocumentIdNum)?.clientId ?? documents[0]?.clientId ?? null;
    const { hasExternalExportEntitlement, hasRoutingCodesEntitlement } = usePlanEntitlements(clientId);

    // Routing codes - fetched once when entitlement is confirmed
    const routingCodesApi = useRoutingCodesApi();
    const [routingCodes, setRoutingCodes] = useState<RoutingCodeDto[]>([]);

    useEffect(() => {
        if (!hasRoutingCodesEntitlement) {
            setRoutingCodes([]);
            return;
        }

        let cancelled = false;

        const load = async () => {
            try {
                const codes = await routingCodesApi.getRoutingCodes();
                if (!cancelled) setRoutingCodes(codes);
            } catch {
                if (!cancelled) setRoutingCodes([]);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [hasRoutingCodesEntitlement, routingCodesApi]);

    // Fetch documents - initial load shows full loading state, refresh is silent
    const fetchDocuments = useCallback(
        async (isInitial = false) => {
            try {
                if (isInitial) {
                    setIsInitialLoading(true);
                } else {
                    setIsRefreshing(true);
                }
                setError(null);

                const response = await documentsApi.getDocuments({
                    limit: 100,
                    offset: 0,
                });

                // Filter out deleted documents and non-inbound documents
                const activeDocuments = response.data.filter((doc) => doc.status !== DocumentStatusEnum.Deleted && doc.isInbound === true);

                setDocuments(activeDocuments);
            } catch (err) {
                console.error("Failed to fetch documents:", err);
                setError(err instanceof Error ? err.message : "Failed to load documents");
            } finally {
                setIsInitialLoading(false);
                setIsRefreshing(false);
            }
        },
        [documentsApi],
    );

    // Fetch document details when selection changes
    const fetchDocumentDetails = useCallback(
        async (id: number) => {
            try {
                const details = await documentsApi.getDocumentById(id);
                setSelectedDocument(details);
            } catch (err) {
                console.error("Failed to fetch document details:", err);
                setSelectedDocument(null);
            }
        },
        [documentsApi],
    );

    // Initial fetch
    useEffect(() => {
        fetchDocuments(true);
    }, [fetchDocuments]);

    // Sync selection from URL
    useEffect(() => {
        if (selectedDocumentIdNum && !isNaN(selectedDocumentIdNum)) {
            fetchDocumentDetails(selectedDocumentIdNum);

            // Also sync checkbox selection if the document is selectable (not error/rejected)
            const selectedDoc = documents.find((doc) => doc.id === selectedDocumentIdNum);
            if (
                selectedDoc &&
                selectedDoc.status !== DocumentStatusEnum.Error &&
                selectedDoc.status !== DocumentStatusEnum.Rejected &&
                !selectedDoc.errorCode &&
                selectedDoc.isProcessingEndStateReached
            ) {
                setSelectedDocumentIds((prev) => {
                    // Only update if not already selected (to preserve multi-selection from clicks)
                    if (prev.size === 0 || !prev.has(selectedDocumentIdNum)) {
                        return new Set([selectedDocumentIdNum]);
                    }
                    return prev;
                });
                setSelectionAnchorId(selectedDocumentIdNum);
            }
        } else {
            setSelectedDocument(null);
        }
        // Reset export status when document changes
        setHasExported(false);
        // Reset unsaved changes when document changes (via URL sync, not user click)
        setHasUnsavedChanges(false);
    }, [selectedDocumentIdNum, fetchDocumentDetails, documents]);

    // Filter documents
    const filteredDocuments = useMemo(() => {
        if (filter === "all") {
            return documents;
        }
        if (filter === DocumentStatusEnum.Error) {
            return documents.filter((doc) => doc.status === DocumentStatusEnum.Error || !!doc.errorCode);
        }
        return documents.filter((doc) => doc.status === filter && !doc.errorCode);
    }, [documents, filter]);

    // Track if we loaded with an explicit document ID to prevent auto-selection from overriding it
    const initialDocumentId = useRef(selectedDocumentId);

    // Auto-select first document when filter changes and current selection is not in filtered list
    // But preserve explicit document ID from URL
    useEffect(() => {
        if (filteredDocuments.length > 0) {
            // If we have a document ID from URL that exists in filtered documents, keep it
            if (selectedDocumentIdNum) {
                const documentExists = filteredDocuments.some((doc) => doc.id === selectedDocumentIdNum);
                if (documentExists) {
                    return; // Keep the URL selection
                }
            }

            // Only auto-select if no document ID was in the initial URL
            if (!initialDocumentId.current && !selectedDocumentIdNum) {
                const firstDoc = filteredDocuments[0];
                setSearchParams({ id: firstDoc.id.toString() });

                // Also sync checkbox selection if the document is selectable (not error/rejected)
                if (
                    firstDoc.status !== DocumentStatusEnum.Error &&
                    firstDoc.status !== DocumentStatusEnum.Rejected &&
                    !firstDoc.errorCode &&
                    firstDoc.isProcessingEndStateReached
                ) {
                    setSelectedDocumentIds(new Set([firstDoc.id]));
                    setSelectionAnchorId(firstDoc.id);
                }
            }
        } else if (!selectedDocumentIdNum) {
            setSearchParams({});
            setSelectedDocument(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredDocuments]);

    // Handle document click with modifier keys (Windows Explorer-like selection)
    const handleDocumentClick = useCallback(
        (documentId: number, event: React.MouseEvent) => {
            const isCtrlPressed = event.ctrlKey || event.metaKey;
            const isShiftPressed = event.shiftKey;

            // Check if there are unsaved changes before switching documents
            if (hasUnsavedChanges && selectedDocumentIdNum !== documentId) {
                event.preventDefault();
                setPendingDocumentId(documentId);
                setShowDiscardDialog(true);
                return;
            }

            // Always update the viewed document
            setSearchParams({ id: documentId.toString() });

            // Find the clicked document to check if it's in error state
            const clickedDocument = filteredDocuments.find((doc) => doc.id === documentId);
            const isErrorDocument =
                clickedDocument &&
                (clickedDocument.status === DocumentStatusEnum.Error ||
                    clickedDocument.status === DocumentStatusEnum.Rejected ||
                    !!clickedDocument.errorCode ||
                    !clickedDocument.isProcessingEndStateReached);

            // Helper to filter out error documents from selection
            const filterErrorDocuments = (ids: number[]) => {
                return ids.filter((id) => {
                    const doc = filteredDocuments.find((d) => d.id === id);
                    return (
                        doc &&
                        doc.status !== DocumentStatusEnum.Error &&
                        doc.status !== DocumentStatusEnum.Rejected &&
                        !doc.errorCode &&
                        doc.isProcessingEndStateReached
                    );
                });
            };

            if (isShiftPressed && selectionAnchorId !== null) {
                // Shift+click: select range from anchor to clicked item
                const anchorIndex = filteredDocuments.findIndex((doc) => doc.id === selectionAnchorId);
                const clickedIndex = filteredDocuments.findIndex((doc) => doc.id === documentId);

                if (anchorIndex !== -1 && clickedIndex !== -1) {
                    const startIndex = Math.min(anchorIndex, clickedIndex);
                    const endIndex = Math.max(anchorIndex, clickedIndex);

                    // Get all document IDs in the range, excluding error documents
                    const rangeIds = filterErrorDocuments(filteredDocuments.slice(startIndex, endIndex + 1).map((doc) => doc.id));

                    if (isCtrlPressed) {
                        // Ctrl+Shift: add range to existing selection
                        setSelectedDocumentIds((prev) => {
                            const newSet = new Set(prev);
                            rangeIds.forEach((id) => newSet.add(id));
                            return newSet;
                        });
                    } else {
                        // Shift only: replace selection with range
                        setSelectedDocumentIds(new Set(rangeIds));
                    }
                }
                // Don't update anchor on shift-click
            } else if (isCtrlPressed) {
                // Ctrl+click: toggle the clicked item, keep others (skip error documents)
                if (!isErrorDocument) {
                    setSelectedDocumentIds((prev) => {
                        const newSet = new Set(prev);
                        if (newSet.has(documentId)) {
                            newSet.delete(documentId);
                        } else {
                            newSet.add(documentId);
                        }
                        return newSet;
                    });
                }
                // Update anchor to clicked item (even for error docs, so shift-click range works)
                setSelectionAnchorId(documentId);
            } else {
                // Normal click: clear all and select only this item (skip error documents)
                if (isErrorDocument) {
                    setSelectedDocumentIds(new Set());
                } else {
                    setSelectedDocumentIds(new Set([documentId]));
                }
                // Set anchor to clicked item
                setSelectionAnchorId(documentId);
            }
        },
        [filteredDocuments, selectionAnchorId, setSearchParams, hasUnsavedChanges, selectedDocumentIdNum],
    );

    // Handle export to CSV
    const handleExport = async (document: DocumentDetailDto) => {
        try {
            setIsExporting(true);
            setExportError(null);

            console.log("Exporting document:", document.id);
            console.log("Document details:", document);

            // Generate filename with timestamp and document type
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);

            // Get document type name
            const documentTypeName = document.documentType?.name || "document";
            const safeTypeName = documentTypeName
                .toLowerCase()
                .replace(/\s+/g, "-") // Replace spaces with dashes
                .replace(/[^a-z0-9-]/g, ""); // Remove special characters

            // Include document ID or filename in the export name for uniqueness
            const docIdentifier = document.fileName?.replace(/\.[^.]+$/, "") || `doc-${document.id}`;
            const safeDocIdentifier = docIdentifier
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "")
                .substring(0, 30); // Limit length

            const filename = `${safeTypeName}-${safeDocIdentifier}-${timestamp}.csv`;

            // Export single document to CSV and trigger download
            exportDocumentsToCsv([document], filename);

            // Update document status to Exported via API
            await documentsApi.batchUpdateStatus({
                documentIds: [document.id],
                status: DocumentStatusEnum.Exported,
            });

            // Refresh the document list to show updated statuses (without showing loading spinner)
            await fetchDocuments(false);

            // Refresh current document details if one is selected
            if (selectedDocumentIdNum) {
                await fetchDocumentDetails(selectedDocumentIdNum);
            }

            // Mark as exported successfully
            setHasExported(true);
        } catch (err) {
            console.error("Failed to export document:", err);
            setExportError("Failed to export document. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    // Handle delete - opens confirmation dialog
    const handleDelete = (document: DocumentDetailDto) => {
        setDocumentToDelete(document);
        setShowDeleteDialog(true);
    };

    // Confirm delete - called from dialog
    const handleConfirmDelete = async () => {
        if (!documentToDelete) return;

        try {
            setIsDeleting(true);
            await documentsApi.deleteDocument(documentToDelete.id);
            // Refresh the document list (without showing loading spinner)
            await fetchDocuments(false);
            // Clear selection if deleted document was selected
            if (documentToDelete.id === selectedDocumentIdNum) {
                setSearchParams({});
                setSelectedDocument(null);
            }
        } catch (err) {
            console.error("Failed to delete document:", err);
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
            setDocumentToDelete(null);
        }
    };

    // Handle checkbox change for individual documents
    const handleDocumentCheckboxChange = useCallback((documentId: number, checked: boolean) => {
        setSelectedDocumentIds((prev) => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(documentId);
            } else {
                newSet.delete(documentId);
            }
            return newSet;
        });
    }, []);

    // Handle select all checkbox
    const handleSelectAll = useCallback(
        (checked: boolean) => {
            if (checked) {
                // Select all filtered documents, excluding error/rejected documents
                const selectableIds = new Set(
                    filteredDocuments
                        .filter(
                            (doc) =>
                                doc.status !== DocumentStatusEnum.Error &&
                                doc.status !== DocumentStatusEnum.Rejected &&
                                !doc.errorCode &&
                                doc.isProcessingEndStateReached,
                        )
                        .map((doc) => doc.id),
                );
                setSelectedDocumentIds(selectableIds);
            } else {
                // Deselect all
                setSelectedDocumentIds(new Set());
            }
        },
        [filteredDocuments],
    );

    // Validate required fields for a list of documents using cached/fetched definitions
    const validateDocumentsRequiredFields = useCallback(
        async (docs: DocumentDetailDto[]): Promise<string[]> => {
            const docsWithMissingFields: string[] = [];

            for (const doc of docs) {
                const docTypeId = doc.documentType?.id;
                if (!docTypeId) continue;

                // Get field definitions (from cache or fetch)
                let definitions = fieldDefinitionsCache.get(docTypeId);
                if (!definitions) {
                    const defResponse = await documentsApi.getCustomFieldDefinitions(docTypeId);
                    definitions = defResponse.customFieldDefinitions;
                    // Cache for future use
                    setFieldDefinitionsCache((prev) => {
                        const newCache = new Map(prev);
                        newCache.set(docTypeId, definitions!);
                        return newCache;
                    });
                }

                // Check required fields
                const hasEmpty = definitions.some((def) => {
                    if (!def.isRequired) return false;
                    const fieldValue = doc.customFieldValues?.find((fv) => fv.customFieldDefinitionId === def.id);
                    if (!fieldValue) return true;
                    const value = getCustomFieldDtoDisplayValue(fieldValue);
                    return (
                        value === null ||
                        value === undefined ||
                        (typeof value === "string" && value.trim() === "") ||
                        (typeof value === "number" && isNaN(value))
                    );
                });

                if (hasEmpty) {
                    docsWithMissingFields.push(doc.generatedName || doc.fileName || `Document #${doc.id}`);
                }
            }

            return docsWithMissingFields;
        },
        [fieldDefinitionsCache, documentsApi],
    );

    // Handle multi-export
    const handleMultiExport = async () => {
        if (selectedDocumentIds.size === 0) {
            return;
        }

        try {
            setIsMultiExporting(true);
            setExportError(null);
            setMultiExportError(null);

            console.log("Multi-exporting documents:", Array.from(selectedDocumentIds));

            // Fetch full details for all selected documents
            const response = await documentsApi.getDocumentsByIds(Array.from(selectedDocumentIds));
            const documentsToExport = response.documents;

            console.log("Fetched documents for export:", documentsToExport);

            if (documentsToExport.length === 0) {
                setMultiExportError("No documents found to export.");
                return;
            }

            // Validate required fields for all documents
            const docsWithMissingFields = await validateDocumentsRequiredFields(documentsToExport);
            if (docsWithMissingFields.length > 0) {
                setMultiExportError(
                    `Cannot export: ${docsWithMissingFields.length} document(s) have missing required fields: ${docsWithMissingFields.slice(0, 3).join(", ")}${docsWithMissingFields.length > 3 ? ` and ${docsWithMissingFields.length - 3} more` : ""}.`,
                );
                return;
            }

            // Export documents grouped by document type
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
            exportDocumentsByType(documentsToExport, timestamp);

            // Update all exported documents' status to Exported
            await documentsApi.batchUpdateStatus({
                documentIds: Array.from(selectedDocumentIds),
                status: DocumentStatusEnum.Exported,
            });

            // Refresh the document list to show updated statuses
            await fetchDocuments(false);

            // Refresh current document details if one is selected
            if (selectedDocumentIdNum) {
                await fetchDocumentDetails(selectedDocumentIdNum);
            }

            console.log("Multi-export completed successfully");
        } catch (err) {
            console.error("Failed to multi-export documents:", err);
            setMultiExportError("Failed to export documents. Please try again.");
        } finally {
            setIsMultiExporting(false);
        }
    };

    // Handle external export (single document)
    const handleExternalExport = async (document: DocumentDetailDto) => {
        try {
            setIsExternalExporting(true);
            await documentsApi.exportDocuments([document.id], "external");
            await documentsApi.batchUpdateStatus({
                documentIds: [document.id],
                status: DocumentStatusEnum.Exported,
            });
            await fetchDocuments(false);
            if (selectedDocumentIdNum === document.id) {
                await fetchDocumentDetails(document.id);
            }
        } catch (err) {
            console.error("Failed to external export document:", err);
            alert("Failed to export document. Please try again.");
        } finally {
            setIsExternalExporting(false);
        }
    };

    // Handle multi external export
    const handleMultiExternalExport = async () => {
        if (selectedDocumentIds.size === 0) return;
        try {
            setIsMultiExternalExporting(true);
            setMultiExportError(null);

            // Fetch full details for all selected documents to validate required fields
            const response = await documentsApi.getDocumentsByIds(Array.from(selectedDocumentIds));
            const documentsToExport = response.documents;

            if (documentsToExport.length === 0) {
                setMultiExportError("No documents found to export.");
                return;
            }

            // Validate required fields for all documents
            const docsWithMissingFields = await validateDocumentsRequiredFields(documentsToExport);
            if (docsWithMissingFields.length > 0) {
                setMultiExportError(
                    `Cannot export: ${docsWithMissingFields.length} document(s) have missing required fields: ${docsWithMissingFields.slice(0, 3).join(", ")}${docsWithMissingFields.length > 3 ? ` and ${docsWithMissingFields.length - 3} more` : ""}.`,
                );
                return;
            }

            await documentsApi.exportDocuments(Array.from(selectedDocumentIds), "external");
            await documentsApi.batchUpdateStatus({
                documentIds: Array.from(selectedDocumentIds),
                status: DocumentStatusEnum.Exported,
            });
            await fetchDocuments(false);
            if (selectedDocumentIdNum) {
                await fetchDocumentDetails(selectedDocumentIdNum);
            }
        } catch (err) {
            console.error("Failed to multi external export:", err);
            setMultiExportError("Failed to export documents. Please try again.");
        } finally {
            setIsMultiExternalExporting(false);
        }
    };

    // Loading state (only for initial load, not background refresh)
    if (isInitialLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <LoadingIndicator type="line-simple" size="md" label="Loading documents..." />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <EmptyState size="sm">
                    <EmptyState.Header pattern="circle">
                        <EmptyState.FeaturedIcon icon={AlertCircle} color="error" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                        <EmptyState.Title>Failed to Load Documents</EmptyState.Title>
                        <EmptyState.Description>{error}</EmptyState.Description>
                    </EmptyState.Content>
                    <EmptyState.Footer>
                        <Button color="primary" onClick={() => fetchDocuments(true)}>
                            Retry
                        </Button>
                    </EmptyState.Footer>
                </EmptyState>
            </div>
        );
    }

    // Empty state
    if (documents.length === 0) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <EmptyState size="md">
                    <EmptyState.Header pattern="circle">
                        <EmptyState.Illustration type="documents" color="gray" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                        <EmptyState.Title>No Documents Found</EmptyState.Title>
                        <EmptyState.Description>Upload documents from the Home page to get started.</EmptyState.Description>
                    </EmptyState.Content>
                </EmptyState>
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 w-full overflow-hidden">
            {/* Left Column - Document List */}
            {!isListCollapsed && (
                <div className="h-full flex-shrink-0">
                    <DocumentListPanel
                        documents={documents}
                        filteredDocuments={filteredDocuments}
                        selectedDocumentId={selectedDocumentIdNum}
                        onDocumentClick={handleDocumentClick}
                        filter={filter}
                        onFilterChange={setFilter}
                        selectedDocumentIds={selectedDocumentIds}
                        onDocumentCheckboxChange={handleDocumentCheckboxChange}
                        onSelectAll={handleSelectAll}
                        onMultiExport={handleMultiExport}
                        isMultiExporting={isMultiExporting}
                        hasExternalExportEntitlement={hasExternalExportEntitlement}
                        onMultiExternalExport={handleMultiExternalExport}
                        isMultiExternalExporting={isMultiExternalExporting}
                        onCollapse={() => setIsListCollapsed(true)}
                        multiExportError={multiExportError}
                    />
                </div>
            )}

            {/* Center Column - PDF Viewer */}
            <div className="h-full min-w-0 flex-1 overflow-hidden">
                <PDFViewerPanel
                    document={selectedDocument}
                    filesApi={filesApi}
                    isListCollapsed={isListCollapsed}
                    onExpandList={() => setIsListCollapsed(false)}
                />
            </div>

            {/* Right Column - Document Details */}
            <div className="h-full flex-shrink-0">
                <DocumentDetailsPanel
                    document={selectedDocument}
                    onExport={handleExport}
                    onExternalExport={handleExternalExport}
                    onDelete={handleDelete}
                    onDocumentUpdate={fetchDocumentDetails}
                    isExporting={isExporting}
                    isExternalExporting={isExternalExporting}
                    hasExported={hasExported}
                    hasExternalExportEntitlement={hasExternalExportEntitlement}
                    routingCodes={routingCodes}
                    onHasChangesChange={setHasUnsavedChanges}
                    exportError={exportError}
                    fieldDefinitionsCache={fieldDefinitionsCache}
                    onFieldDefinitionsCacheUpdate={handleFieldDefinitionsCacheUpdate}
                />
            </div>

            {/* Discard Changes Confirmation Dialog */}
            {showDiscardDialog && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <h2 className="mb-2 text-lg font-semibold text-primary">Discard Changes?</h2>
                                <p className="mb-6 text-tertiary">
                                    You have unsaved changes. Are you sure you want to switch documents? Your changes will be lost.
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        color="secondary"
                                        size="md"
                                        onClick={() => {
                                            setShowDiscardDialog(false);
                                            setPendingDocumentId(null);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="primary-destructive"
                                        size="md"
                                        onClick={() => {
                                            setHasUnsavedChanges(false);
                                            setShowDiscardDialog(false);
                                            if (pendingDocumentId !== null) {
                                                setSearchParams({ id: pendingDocumentId.toString() });
                                                setPendingDocumentId(null);
                                            }
                                        }}
                                    >
                                        Discard Changes
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Delete Document Confirmation Dialog */}
            {showDeleteDialog && documentToDelete && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <div className="mb-4">
                                    <FeaturedIcon icon={Trash01} color="error" theme="light" size="md" />
                                </div>
                                <h2 className="mb-2 text-lg font-semibold text-primary">Delete Document?</h2>
                                <p className="mb-6 text-tertiary">
                                    Are you sure you want to delete &ldquo;{documentToDelete.fileName || `Document #${documentToDelete.id}`}&rdquo;? This action
                                    cannot be undone.
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        color="secondary"
                                        size="md"
                                        onClick={() => {
                                            setShowDeleteDialog(false);
                                            setDocumentToDelete(null);
                                        }}
                                        isDisabled={isDeleting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button color="primary-destructive" size="md" iconLeading={Trash01} onClick={handleConfirmDelete} isLoading={isDeleting}>
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
}
